"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { GROQButton } from "@/sanity/lib/definitions";
import { stegaClean } from "@sanity/client/stega";
import { Slot } from "@radix-ui/react-slot";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import { iconMap } from "@/lib/iconMap";
import { useCTAWrapper } from "@/hooks/useCTAWrapper";

const buttonVariants = cva("button cta", {
  variants: {
    variant: {
      default: "button-default",
      orange: "button-orange",
    },
    size: {
      default: "button-size-default",
      sm: "button-size-small",
    },
    shape: {
      default: "button-shape-default",
    },
    action: {
      link: "",
      popup: "",
      anchor: "",
      none: "",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
    shape: "default",
    action: "none",
  },
});

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, shape, action, size, asChild = false, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const children = props.children;
    return (
      <Comp
        className={cn(
          "group",
          buttonVariants({ variant, size, shape, className }),
        )}
        data-type={variant}
        data-size={size}
        data-shape={shape}
        data-action={action}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export interface CTAButtonProps extends React.HTMLAttributes<HTMLElement> {
  data: GROQButton;
  overrideText?: string;
  overrideVariant?: "default" | "orange";
  animateText?: boolean;
  showIncomplete?: boolean;
}

const CTAButton = React.forwardRef<HTMLElement, CTAButtonProps>(
  (
    {
      className,
      data,
      overrideText,
      overrideVariant,
      animateText = true,
      showIncomplete = false,
      ...props
    },
    ref,
  ) => {
    const cleanedData = stegaClean(data ?? {}) ?? null;

    const type = overrideVariant ?? cleanedData?.type ?? "default";
    const size = "default" as const;
    const shape = "default" as const;

    const hasIcon = cleanedData?.cta?.icon ?? "none";
    const icon = iconMap[hasIcon as keyof typeof iconMap];

    const action = cleanedData?.cta?.action ?? null;

    const { isLink, link, isAnchor, anchor, isPopup, popup } = useCTAWrapper(
      cleanedData?.cta,
    );
    const title = cleanedData?.cta?.title?.trim() ?? "";
    const canAct = Boolean(
      (isLink && link) || (isAnchor && anchor) || (isPopup && popup),
    );
    const incomplete = showIncomplete && (!title || !canAct);

    const buttonText =
      overrideText ??
      (incomplete
        ? title || "Please select a CTA"
        : data?.cta
          ? (data.cta.title ?? "Please add a title")
          : "Please select a CTA");

    const buttonRef = React.useRef<HTMLSpanElement>(null);
    const [iconSize, setIconSize] = React.useState<number | null>(null);

    React.useEffect(() => {
      const el = buttonRef.current;
      if (!el || !icon) return;

      const observer = new ResizeObserver(([entry]) => {
        setIconSize(
          entry.contentRect.height +
            parseFloat(getComputedStyle(el).paddingTop) * 2,
        );
      });
      observer.observe(el);
      return () => observer.disconnect();
    }, [icon]);

    if (incomplete && (!cleanedData?.type || !cleanedData?.shape)) return null;

    return (
      <CTAWrapper
        cta={incomplete ? null : cleanedData?.cta}
        ref={ref}
        {...props}
        aria-disabled={incomplete || undefined}
        className={cn(
          "cta-wrapper flex items-center relative z-10 w-full sm:w-auto gap-[.5rem]",
          incomplete && "pointer-events-none cursor-not-allowed opacity-50",
          className,
        )}
      >
        <span
          ref={buttonRef}
          className={cn(
            "group flex relative items-center w-full h-full gap-[1.25rem] lg:w-fit",
            buttonVariants({ variant: type, size, shape }),
            !animateText &&
              "[&_.button-text-initial]:!transform-none [&_.button-text-initial]:!opacity-100",
          )}
          data-type={type}
          data-size={size}
          data-shape={shape}
          data-action={action ?? "none"}
        >
          <span className="relative button-text overflow-clip">
            <span className="block transition-[transform,opacity] duration-300 pr-[.5rem] button-text-initial">
              {buttonText}
            </span>
            {animateText && (
              <span
                aria-hidden="true"
                className="block absolute left-0 transition-[transform,opacity] duration-300 pointer-events-none button-text-hover top-[3rem] pr-[.5rem]"
              >
                {buttonText}
              </span>
            )}
          </span>
        </span>

        {icon && hasIcon && (
          <span
            aria-hidden="true"
            className={cn(
              "button-icon-box button-shape-default relative shrink-0 flex items-center justify-center overflow-clip [&_svg]:w-[3rem] [&_svg]:h-[3rem] [&_svg]:shrink-0",
              type === "orange" ? "button-orange" : "button-default",
            )}
            style={iconSize ? { width: iconSize, height: iconSize } : undefined}
          >
            <span className="flex justify-center items-center w-full h-full transition-all duration-300 button-icon-initial">
              {icon}
            </span>
            <span className="button-icon-hover absolute inset-0 flex items-center justify-center -translate-x-full transition-all duration-300 pointer-events-none [&_svg]:w-[3rem] [&_svg]:h-[3rem]">
              {icon}
            </span>
          </span>
        )}
      </CTAWrapper>
    );
  },
);
CTAButton.displayName = "CTAButton";

export { Button, buttonVariants, CTAButton };
